/**
 * Platform (superuser) API for SGOKit staff.
 *
 * These hit `/api/platform/*`, a platform path that carries no X-TENANT header:
 * the platform-wide state roster (ProgramState) is not tenant-scoped: every
 * tenant reads it live through its StateConfig FK, so a write here changes all of
 * them with nothing to fan out. Guarded by IsSuperuser.
 * Uses the shared axios client, which attaches the token.
 */
import apiClient from './client';

export interface ProgramState {
  abbreviated_name: string;
  full_name: string;
  opted_in: boolean;
  eligible_school_types: string[];
}

export type ProgramStatePatch = Partial<
  Pick<ProgramState, 'opted_in' | 'eligible_school_types'>
>;

export type PlatformStateProperties = {
  full_name: string;
  abbreviated_name: string;
  opted_in: boolean;
  /** [longitude, latitude] inside the state's drawn shape (AK/HI are insets). */
  label_point: [number, number];
};

export type PlatformStateMapData = GeoJSON.FeatureCollection<
  GeoJSON.MultiPolygon | GeoJSON.Polygon,
  PlatformStateProperties
> & { bbox: [number, number, number, number] };

interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const platformApi = {
  /** Public (no auth, no tenant): the national opt-in map on the homepage. */
  getStateMap: (): Promise<PlatformStateMapData> =>
    apiClient.get('/platform/states/map/').then((r) => r.data),

  listProgramStates: (): Promise<ProgramState[]> =>
    // The roster is 51 rows; PAGE_SIZE (60) returns them all on one page.
    apiClient
      .get('/platform/states/')
      .then((r) => (r.data as Paginated<ProgramState>).results),

  updateProgramState: (code: string, patch: ProgramStatePatch): Promise<ProgramState> =>
    apiClient.patch(`/platform/states/${code}/`, patch).then((r) => r.data),
};
